"use client";

import { useRef, useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PrevNextNav, { getSuccessStoryNav } from "@/components/PrevNextNav";

function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) groupRef.current.rotation.y = t * 0.01;
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.05;
      mesh1Ref.current.position.y = Math.sin(t * 0.2) * 0.3;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.04;
      mesh2Ref.current.position.y = Math.sin(t * 0.25 + 1) * 0.25;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={mesh1Ref} position={[3, 0.5, -2]}>
        <octahedronGeometry args={[1.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.2} transparent />
      </mesh>
      <mesh ref={mesh2Ref} position={[-2.5, -0.5, -1]}>
        <icosahedronGeometry args={[1.2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.15} transparent />
      </mesh>
    </group>
  );
}

function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);
  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) pointsRef.current.rotation.z = state.clock.elapsedTime * 0.005;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

function AnimatedSection({
  children,
  className = "",
  delay = 0,
  waitFor = true
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  waitFor?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!waitFor) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [waitFor]);

  return (
    <section
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.6s ease-out ${delay}ms, transform 0.6s ease-out ${delay}ms`,
      }}
    >
      {children}
    </section>
  );
}

const project = {
  title: "Frontier Airlines",
  subtitle: "Content Infrastructure Modernization",
  subtitle2: "Building a unified content foundation for consistent passenger experiences across channels",
  heroImage: "/images/work/Frontier-Airlines.jpg",
  challenge: "Fragmented content across web, mobile, and transactional platforms made it difficult for teams to locate, update, and reuse content efficiently.",
  solution: "A comprehensive content audit and modular content architecture designed to support migration to Sanity CMS.",
  results: "A unified content foundation enabling consistent messaging across web, mobile, and transactional passenger touchpoints.",
  challengeAndApproach: [
    "Frontier Airlines managed content across web, mobile, transactional, and internal platforms, with each channel operating independently. This fragmentation led to duplicated content, inconsistent messaging, and inefficient editorial workflows.",
    "Mapleton Hill conducted a full-scale content audit and designed a modular content architecture within Sanity CMS. The new foundation enables structured content reuse, streamlined governance, and scalable delivery across all digital touchpoints.",
  ],
  capabilities: [
    "Content Strategy & Auditing",
    "Content Modeling & Architecture",
    "Cross-Channel Content Management",
    "CMS Migration Planning",
  ],
  outcomes: [
    { title: "Content Reuse", description: "Enabled consistent content reuse across all digital channels" },
    { title: "Streamlined Workflows", description: "Simplified editorial workflows for distributed teams" },
    { title: "Reduced Duplication", description: "Eliminated redundant content" },
    { title: "Scalable Growth", description: "Future-ready infrastructure" },
  ],
  outcomesIntro: "The content modernization initiative delivered measurable improvements:",
  conclusion: "Frontier Airlines' content modernization initiative demonstrates how strategic content architecture can transform fragmented content systems into a unified, scalable foundation for cross-channel passenger communication.",
  closingImage: "/images/success-stories/Frontier.jpg",
};

export default function FrontierPage() {
  const [glowPos, setGlowPos] = useState({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      targetPos.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      targetPos.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    let animationId: number;
    const currentPos = { x: 0, y: 0 };
    const animate = () => {
      currentPos.x += (targetPos.current.x - currentPos.x) * 0.03;
      currentPos.y += (targetPos.current.y - currentPos.y) * 0.03;
      setGlowPos({ x: currentPos.x, y: currentPos.y });
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden">
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10">
        <Navigation />

        <main id="main-content">

        <section className="pt-28 pb-8 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4">
              <Link href="/work" className="inline-flex items-center gap-2 text-sm text-[#1a1a1a]/70 hover:text-[#ff6900] transition-colors mb-4">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Work
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight mb-4">
                {project.title}
              </h1>
              <p className="text-xl text-[#ff6900] font-medium">
                {project.subtitle}
              </p>
              <p className="text-lg text-[#1a1a1a]/70 mt-2">
                {project.subtitle2}
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl h-[400px]">
              <img src={project.heroImage} alt={project.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>

        {/* Challenge / Solution / Results - Icon Cards */}
        <AnimatedSection className="py-16 px-6" >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="group bg-white rounded-3xl p-8 border border-[#1a1a1a]/5 hover:border-[#ff6900]/20 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#ff6900]/10 flex items-center justify-center mb-6 group-hover:bg-[#ff6900]/20 transition-colors">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-3">Challenge</h3>
                <p className="text-[#1a1a1a]/70 leading-relaxed">{project.challenge}</p>
              </div>
              <div className="group bg-white rounded-3xl p-8 border border-[#1a1a1a]/5 hover:border-[#ff6900]/20 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#ff6900]/10 flex items-center justify-center mb-6 group-hover:bg-[#ff6900]/20 transition-colors">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-3">Solution</h3>
                <p className="text-[#1a1a1a]/70 leading-relaxed">{project.solution}</p>
              </div>
              <div className="group bg-[#1a1a1a] rounded-3xl p-8 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-[#ff6900]/20 flex items-center justify-center mb-6 group-hover:bg-[#ff6900]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-3">Results</h3>
                <p className="text-white/70 leading-relaxed">{project.results}</p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Outcomes */}
        <AnimatedSection className="py-16 md:py-24 px-6" delay={100} >
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-4 text-center">Outcomes</h2>
            <p className="text-lg text-[#1a1a1a]/70 text-center mb-12">{project.outcomesIntro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {project.outcomes.map((outcome, index) => (
                <div key={index} className="group p-6 rounded-2xl bg-white border border-[#1a1a1a]/5 hover:shadow-lg hover:border-[#ff6900]/20 transition-all duration-300">
                  <span className="inline-block text-sm font-bold text-[#ff6900]/50 group-hover:text-[#ff6900] transition-colors mb-3">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-lg font-semibold mb-2">{outcome.title}</h3>
                  <p className="text-[#1a1a1a]/70">{outcome.description}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* The Challenge and Approach */}
        <AnimatedSection className="py-16 px-6 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]" delay={100} >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-6">The Challenge and Approach</h2>
            {project.challengeAndApproach.map((paragraph, index) => (
              <p key={index} className="text-xl md:text-2xl text-white/80 leading-relaxed font-light mb-6 last:mb-0">
                {paragraph}
              </p>
            ))}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {project.capabilities.map((capability) => (
                <span key={capability} className="px-4 py-2 bg-white/10 rounded-full text-white/70">{capability}</span>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Conclusion / Partnership */}
        <AnimatedSection className="py-16 px-6 bg-white" delay={100} >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-6">Partnership</h2>
            <p className="text-xl text-[#1a1a1a]/70 leading-relaxed">
              {project.conclusion}
            </p>
          </div>
        </AnimatedSection>

        {/* Closing Image */}
        <AnimatedSection className="px-6 py-16" delay={100} >
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={project.closingImage} alt={project.title} className="w-full h-auto" />
            </div>
          </div>
        </AnimatedSection>

        <PrevNextNav
          {...getSuccessStoryNav("/success-story/frontier-airlines")}
          basePath="/work"
          baseLabel="All Work"
        />

        <section className="py-24 px-6 bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">Ready to start your project?</h2>
            <p className="text-xl text-white/70 mb-10">Let&apos;s discuss how we can help bring your ideas to life.</p>
            <Link href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#1a1a1a] font-medium hover:bg-white/90 transition-colors">
              Get in touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>

        </main>

        <Footer />
      </div>
    </div>
  );
}
